import React from "react";
import * as Lucide from "lucide-react";
import tokens from "../tokens/tokens.json";
type ColorKey = keyof typeof tokens.color.alias;

// Type guard to check if an object has a $value property
const hasValue = (obj: unknown): obj is { $value: string } => {
  return obj !== null && typeof obj === "object" && "$value" in obj;
};

// Resolve token alias string like "rgb({color.neutral.00})" or "rgba({theme.default.primary.60}, 0.5)"
const resolveColorValue = (colorKey: ColorKey): string => {
  const colorToken = tokens.color.alias[colorKey];
  if (!colorToken || !hasValue(colorToken)) return "currentColor";

  let value = colorToken.$value;

  // If already a concrete color (hex, rgb, rgba), return as-is
  const lower = value.toLowerCase();
  if (
    lower.startsWith("#") ||
    lower.startsWith("rgb(") ||
    lower.startsWith("rgba(")
  ) {
    // Still might contain {refs} inside rgb/rgba - replace them if present
    // Fall through to replacement logic
  }

  // Replace all {path.to.token} occurrences with their resolved $value
  const TOKEN_REF_REGEX = /\{([^}]+)\}/g;

  const resolvePath = (path: string): string | undefined => {
    const segments = path.split(".");
    let cursor: unknown = tokens as unknown as Record<string, unknown>;
    for (const seg of segments) {
      if (
        cursor &&
        typeof cursor === "object" &&
        seg in (cursor as Record<string, unknown>)
      ) {
        cursor = (cursor as Record<string, unknown>)[seg];
      } else {
        return undefined;
      }
    }
    if (hasValue(cursor)) return cursor.$value;
    return undefined;
  };

  value = value.replace(TOKEN_REF_REGEX, (_, path: string) => {
    const resolved = resolvePath(path);
    return resolved !== undefined ? String(resolved) : "currentColor";
  });

  // If replacement didn't happen and we still don't have a concrete value, fallback
  const finalLower = value.toLowerCase();
  if (
    !(
      finalLower.startsWith("#") ||
      finalLower.startsWith("rgb(") ||
      finalLower.startsWith("rgba(")
    )
  ) {
    return "currentColor";
  }

  return value;
};

export type IconName = keyof typeof Lucide;

export interface IconProps {
  name: IconName;
  color?: ColorKey; // If undefined, icon will inherit CSS currentColor
  className?: string;
  size?: number | string; // Size prop for icon scaling
}

export const Icon: React.FC<IconProps> = ({
  name,
  color,
  className,
  size = "1em",
}) => {
  const IconComponent = Lucide[name];
  // Only render if it's a valid React component (forwardRef object)
  if (
    !IconComponent ||
    typeof IconComponent !== "object" ||
    !("render" in IconComponent || "$$typeof" in IconComponent)
  ) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`Lucide icon "${name}" is not a valid React component.`);
    }
    return null;
  }

  // Base icon scales with font-size via 1em width/height.
  const iconColor = color ? resolveColorValue(color) : undefined;

  // Explicitly cast to unknown first, then to React.JSXElementConstructor with proper props
  const Component = IconComponent as unknown as React.JSXElementConstructor<{
    size?: number | string;
    color?: string;
    className?: string;
    style?: React.CSSProperties;
    "aria-hidden"?: string;
  }>;

  return (
    <Component
      size={size}
      color={iconColor}
      className={className}
      // No fontSize here; allow parents to control size via font-size
      aria-hidden="true"
    />
  );
};
